import { useState } from "react";
import Modal from "@/components/base/Modal";
import { supabase } from "@/lib/supabase";
import { logAudit } from "@/lib/audit";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import type { Brand, Category } from "@/types";

interface CatalogManagerModalProps {
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
  categories: Category[];
  brands: Brand[];
}

type Segment = "categories" | "brands";

export default function CatalogManagerModal({
  open,
  onClose,
  onChanged,
  categories,
  brands,
}: CatalogManagerModalProps) {
  const toast = useToast();
  const { profile } = useAuth();
  const [segment, setSegment] = useState<Segment>("categories");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const resetForm = () => {
    setName("");
    setDescription("");
    setEditingId(null);
  };

  const audit = async (action: string, entity: string, entityId: string, value: unknown) => {
    await logAudit({
      userId: profile?.id ?? null,
      userName: `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario",
      action,
      entity,
      entityId,
      newValue: value as Record<string, unknown>,
    });
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Escribe un nombre.");
      return;
    }
    setBusy(true);
    try {
      if (segment === "categories") {
        if (editingId) {
          const { error } = await supabase
            .from("product_categories")
            .update({ name: name.trim(), description: description.trim() || null })
            .eq("id", editingId);
          if (error) throw error;
          await audit("editar", "product_categories", editingId, { name });
        } else {
          const { data, error } = await supabase
            .from("product_categories")
            .insert({ name: name.trim(), description: description.trim() || null })
            .select("id")
            .single();
          if (error || !data) throw error;
          await audit("crear", "product_categories", data.id, { name });
        }
      } else if (editingId) {
        const { error } = await supabase.from("brands").update({ name: name.trim() }).eq("id", editingId);
        if (error) throw error;
        await audit("editar", "brands", editingId, { name });
      } else {
        const { data, error } = await supabase
          .from("brands")
          .insert({ name: name.trim() })
          .select("id")
          .single();
        if (error || !data) throw error;
        await audit("crear", "brands", data.id, { name });
      }
      toast.success(editingId ? "Registro actualizado." : "Registro creado.");
      resetForm();
      onChanged();
    } catch {
      toast.error("No se pudo guardar. Revisa los datos.");
    } finally {
      setBusy(false);
    }
  };

  const handleToggle = async (id: string, active: boolean, type: Segment) => {
    const table = type === "categories" ? "product_categories" : "brands";
    const { error } = await supabase.from(table).update({ active: !active }).eq("id", id);
    if (error) {
      toast.error("No se pudo actualizar el estado.");
      return;
    }
    await audit("editar", table, id, { active: !active });
    onChanged();
  };

  const startEdit = (id: string, value: string, desc?: string | null) => {
    setEditingId(id);
    setName(value);
    setDescription(desc ?? "");
  };

  const list = segment === "categories" ? categories : brands;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title="Categorías y marcas"
      subtitle="Organiza el catálogo de productos. Puedes activar o desactivar cada registro."
    >
      <div className="flex flex-col gap-5">
        <div className="flex w-full items-center gap-1 rounded-full border border-background-300 bg-background-100 p-1">
          <button
            type="button"
            onClick={() => {
              setSegment("categories");
              resetForm();
            }}
            className={`flex-1 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
              segment === "categories"
                ? "bg-primary-500 text-background-50"
                : "text-foreground-600 hover:bg-background-200"
            }`}
          >
            Categorías
          </button>
          <button
            type="button"
            onClick={() => {
              setSegment("brands");
              resetForm();
            }}
            className={`flex-1 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
              segment === "brands"
                ? "bg-primary-500 text-background-50"
                : "text-foreground-600 hover:bg-background-200"
            }`}
          >
            Marcas
          </button>
        </div>

        <div className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-4">
          <div className="flex flex-col gap-3 sm:flex-row">
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={segment === "categories" ? "Nombre de la categoría" : "Nombre de la marca"}
              className="flex-1 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none placeholder:text-foreground-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
            {segment === "categories" ? (
              <input
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Descripción (opcional)"
                className="flex-1 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none placeholder:text-foreground-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={busy}
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-70"
            >
              {busy ? (
                <i className="ri-loader-4-line animate-spin text-base"></i>
              ) : (
                <i className={`${editingId ? "ri-save-3-line" : "ri-add-line"} text-base`}></i>
              )}
              {editingId ? "Guardar" : "Agregar"}
            </button>
            {editingId ? (
              <button
                type="button"
                onClick={resetForm}
                className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
              >
                Cancelar
              </button>
            ) : null}
          </div>
        </div>

        <ul className="flex flex-col gap-2">
          {list.length === 0 ? (
            <li className="rounded-md border border-dashed border-background-300 px-4 py-8 text-center text-sm text-foreground-500">
              Sin registros todavía.
            </li>
          ) : (
            list.map((item) => (
              <li
                key={item.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-foreground-950">{item.name}</p>
                  {"description" in item && item.description ? (
                    <p className="truncate text-[11px] text-foreground-400">{item.description}</p>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      startEdit(
                        item.id,
                        item.name,
                        "description" in item ? item.description : null,
                      )
                    }
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 transition-colors hover:bg-background-100"
                  >
                    <i className="ri-edit-2-line text-sm"></i>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleToggle(item.id, item.active, segment)}
                    className={`flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                      item.active
                        ? "border-accent-300 bg-accent-50 text-accent-900"
                        : "border-background-300 bg-background-100 text-foreground-500"
                    }`}
                  >
                    <i className={`${item.active ? "ri-checkbox-circle-line" : "ri-close-circle-line"} text-sm`}></i>
                    {item.active ? "Activo" : "Inactivo"}
                  </button>
                </div>
              </li>
            ))
          )}
        </ul>
      </div>
    </Modal>
  );
}